import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useState } from 'react'

import { estimateAiAnnotate, startAiAnnotate, type AiAnnotateRequest } from '@/api/jobs'
import { keys } from '@/api/keys'
import { isApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { formatDuration, formatUsd, plural } from '@/lib/format'
import { AnnotationModelSelector, useAnnotationModel } from '@/features/annotator/AnnotationModelSelector'

/**
 * "Let Claude look at these" — the confirm step before a bulk AI run.
 *
 * The dialog exists because the run costs real money and real minutes. It
 * shows the ledger-based estimate (`POST /api/annotate/estimate`) before the
 * button, says plainly when there is **no data yet** rather than inventing a
 * number, and never starts anything the operator did not press.
 */

export type AiModel = NonNullable<AiAnnotateRequest['model']>
export type AiEffort = NonNullable<AiAnnotateRequest['effort']>

const EFFORTS: { value: AiEffort; label: string; hint: string }[] = [
  { value: 'low', label: 'Quick look', hint: 'Fewer turns. Fine for simple plates.' },
  { value: 'medium', label: 'Normal', hint: 'The default.' },
  { value: 'high', label: 'Careful', hint: 'More turns, more cost, better boundaries.' },
]

export interface AutoAnnotateDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  uids: string[]
  /** Called with the parent job id once the run is accepted. */
  onStarted?(parentJobId: number): void
}

export function AutoAnnotateDialog({ open, onOpenChange, uids, onStarted }: AutoAnnotateDialogProps) {
  const qc = useQueryClient()
  const aiModel = useAnnotationModel(open)
  const [effort, setEffort] = useState<AiEffort>('medium')
  const [budget, setBudget] = useState('')

  const estimate = useQuery({
    queryKey: ['annotate', 'estimate', uids, aiModel.selection, effort],
    queryFn: () => estimateAiAnnotate({ uids, ...aiModel.selection, effort }),
    enabled: open && uids.length > 0 && aiModel.ready,
    staleTime: 60_000,
    retry: 0,
  })

  const budgetUsd = budget.trim() === '' ? undefined : Number(budget)
  const budgetInvalid = budgetUsd !== undefined && (!Number.isFinite(budgetUsd) || budgetUsd <= 0)

  const start = useMutation({
    mutationFn: () =>
      startAiAnnotate({
        uids,
        ...aiModel.selection,
        effort,
        budget_usd: budgetInvalid ? undefined : budgetUsd,
        skip_if_pending: true,
      }),
    onSuccess: (res) => {
      toast.success(`AI is looking at ${uids.length} ${plural(uids.length, 'asset')}`, {
        description: 'Follow it on the Jobs page; suggestions land in Review.',
      })
      void qc.invalidateQueries({ queryKey: keys.jobs.all() })
      onOpenChange(false)
      onStarted?.(res.parent_job_id)
    },
    onError: (err) => {
      if (isApiError(err) && err.isAiUnavailable) {
        toast.error('The selected AI provider is not available right now', { description: err.message })
        return
      }
      if (isApiError(err) && err.isConflict) {
        toast.warning('Some of these are already queued', { description: err.message })
        return
      }
      toast.error('Could not start the run', { description: err instanceof Error ? err.message : String(err) })
    },
  })

  const est = estimate.data

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Let AI annotate {uids.length} {plural(uids.length, 'asset')}</DialogTitle>
          <DialogDescription>
            AI looks at each picture and suggests regions, anchors and pivots. Nothing is changed until you accept a suggestion in Review.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <AnnotationModelSelector model={aiModel} disabled={start.isPending} />

          <Field label="How hard to look" hint={EFFORTS.find((e) => e.value === effort)?.hint}>
            <Select value={effort} onValueChange={(v) => setEffort(v as AiEffort)}>
              <SelectTrigger aria-label="Effort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EFFORTS.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="Stop each call at (US$)"
            hint="Optional ceiling per picture. Leave empty to use the studio default."
            error={budgetInvalid ? 'Enter a number bigger than zero, or leave it empty.' : undefined}
          >
            <Input
              inputMode="decimal"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="0.60"
              aria-invalid={budgetInvalid || undefined}
              aria-label="Budget per call in US dollars"
            />
          </Field>

          <div className="rounded-xl border border-line bg-paper-2 p-3 text-sm" aria-live="polite">
            {estimate.isPending ? (
              <p className="text-ink-2">Working out what this will cost…</p>
            ) : estimate.isError ? (
              <p className="text-ink-2">No estimate available. The run will still work.</p>
            ) : est ? (
              <>
                <p className="font-medium text-ink">
                  {est.calls} {plural(est.calls, 'call')} ·{' '}
                  {est.usd == null ? 'cost unknown so far' : `about ${formatUsd(est.usd)}`}
                  {est.seconds ? ` · about ${formatDuration(est.seconds)}` : ''}
                </p>
                <p className="mt-1 text-xs text-ink-3">
                  {est.usd == null
                    ? est.note === 'no data yet'
                      ? 'The studio has not run enough of these to guess a price yet.'
                      : (est.note ?? 'No price data yet.')
                    : 'A list-price estimate from past runs, not a bill.'}
                </p>
              </>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={start.isPending} disabled={uids.length === 0 || !aiModel.ready || budgetInvalid} onClick={() => start.mutate()}>
            <Sparkles aria-hidden />
            Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
